// src/pages/BillingBook.js

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  MenuItem,
  IconButton,
  Badge
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  HelpOutline as HelpIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

const API_URL           = "https://htm.bayex.lk/api/save_billing_book.php";
const GET_ADV_API       = "https://htm.bayex.lk/api/get_advance_salary.php";
const SAVE_ADV_API      = "https://htm.bayex.lk/api/save_advance_salary.php";
const DELETE_ADV_API    = "https://htm.bayex.lk/api/delete_advance_salary.php";
const GET_CASH_API      = "https://htm.bayex.lk/api/get_cash_count.php";
const SAVE_CASH_API     = "https://htm.bayex.lk/api/save_cash_count.php";
const STAFF_API         = "https://htm.bayex.lk/api/get_staff.php";


// helper to format "2024-05-02" → "2nd of May 2024"
function formatDateVerbose(isoDate) {
  const [y, m, d] = isoDate.split('-').map(Number);
  if (!y || !m || !d) return '';
  const ord = n => {
    if (n % 100 >= 11 && n % 100 <= 13) return 'th';
    if (n % 10 === 1) return 'st';
    if (n % 10 === 2) return 'nd';
    if (n % 10 === 3) return 'rd';
    return 'th';
  };
  const monthName = new Date(y, m - 1).toLocaleString('default', { month: 'long' });
  return `${d}${ord(d)} of ${monthName} ${y}`;
}

const DESCRIPTION_PRESETS = [
  "Amex Card", "Credit Return", "Credit Sale", "DCC",
  "Master/VISA Card", "Morning Purchase", "Purchase",
  "Website", "PayHere",
];

export default function BillingBook() {
  const theme    = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // number formatter
  const fmt = num => Number(num || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  // friendly label for “type”
  const typeLabel = t => {
    if (t === 'card')    return 'Card / Online';
    if (t === 'expense') return 'Subtract';
    if (t === 'income')  return 'Add';
    return t;
  };

  // ───────────────────────────────────────────────────────────────────────────
  // State
  const [billDate, setBillDate]                       = useState(new Date().toISOString().split('T')[0]);
  const [totalSalesToday, setTotalSalesToday]         = useState('');
  const [totalTips, setTotalTips]                     = useState('');
  const [totalServiceCharges, setTotalServiceCharges] = useState('');
  const [entries, setEntries]                         = useState([]);
  const [advances, setAdvances]                       = useState([]);
  const [staffList, setStaffList]                     = useState([]);
  const [advListOpen, setAdvListOpen]                 = useState(false);

  // Add / Edit Advance
  const [advEditOpen, setAdvEditOpen] = useState(false);
  const [advEditId,   setAdvEditId]   = useState(null);
  const [newAdvance,  setNewAdvance]  = useState({ staff_id: '', amount: '' });

  // Entry modal
  const [entryModalOpen, setEntryModalOpen] = useState(false);
  const [entryEditIndex, setEntryEditIndex] = useState(-1);
  const [newEntry, setNewEntry] = useState({
    type: 'income',
    amount: '',
    description: '',
    isOther: false,          // to track “Other” vs preset
  });

  const [loading, setLoading]       = useState(false);
  const [actualCash, setActualCash] = useState('');
  const [cashDiff, setCashDiff]     = useState(0);

  // ───────────────────────────────────────────────────────────────────────────
  // Load summary, entries & advances & cash whenever the date changes
  useEffect(() => {
    setLoading(true);

    // 1) Load billing_book summary (entries, totals) for that date
    fetch(`${API_URL}?summary_date=${billDate}`)
      .then(r => r.json())
      .then(data => {
        if (data.bill_date) {
          setTotalSalesToday(data.total_sales_today);
          setTotalTips(data.total_tips || '');
          setTotalServiceCharges(data.total_service_charges || '');
          setEntries(data.entries ? JSON.parse(data.entries) : []);
        } else {
          // no record found for that date
          setTotalSalesToday('');
          setTotalTips('');
          setTotalServiceCharges('');
          setEntries([]);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));

    // 2) Load advances for that date
    fetch(`${GET_ADV_API}?date=${billDate}`)
      .then(r => r.json())
      .then(setAdvances)
      .catch(() => {});

    // 3) Load cash count for that date
    fetch(`${GET_CASH_API}?date=${billDate}`)
      .then(r => r.json())
      .then(d => setActualCash(d.actual_cash != null ? String(d.actual_cash) : ''))
      .catch(() => setActualCash(''));
  }, [billDate]);

  // ───────────────────────────────────────────────────────────────────────────
  // Load staff list once
  useEffect(() => {
    fetch(STAFF_API)
      .then(r => r.json())
      .then(setStaffList)
      .catch(() => {});
  }, []);

  // ───────────────────────────────────────────────────────────────────────────
  // Compute Totals (with correct “subtractions”/“revenue” logic)

  // 1) totalSales (reference)
  const refTotal = parseFloat(totalSalesToday) || 0;

  // 2) “Additions” = sum of any entries with type === 'income'
  const incTotal = entries
    .filter(e => e.type === 'income')
    .reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);

  // 3) “All‐expenses+cards” (including advance‐entries too)
  const expTotalAll = entries
    .filter(e => e.type === 'expense' || e.type === 'card')
    .reduce((sum, e) => sum + parseFloat(e.amount || 0), 0);

  // 4) “Advances” total (we keep these separately)
  const advTotal = advances.reduce((sum, a) => sum + parseFloat(a.amount || 0), 0);

  // 5) “Subtractions (excluding advances)” = All expense+card minus advTotal
  const expNonAdv = expTotalAll - advTotal;

  // 6) “Revenue” = refTotal − (expNonAdv) + incTotal
  const revenue = refTotal - expNonAdv + incTotal;

  // 7) “Net Revenue” = revenue − advTotal
  const netRevenue = revenue - advTotal;

  // 8) Update cash difference when either actualCash or netRevenue changes
  useEffect(() => {
    const ac = parseFloat(actualCash) || 0;
    setCashDiff(ac - netRevenue);
  }, [actualCash, netRevenue]);

  // ───────────────────────────────────────────────────────────────────────────
  // Save cash count
  const saveCashCount = () => {
    if (actualCash === '') {
      return alert("Enter actual cash");
    }
    fetch(SAVE_CASH_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ date: billDate, actual_cash: parseFloat(actualCash) })
    })
      .then(r => r.json())
      .then(d => {
        if (d.status === 'success') {
          alert("Cash count saved");
        } else {
          alert("Error: " + d.message);
        }
      })
      .catch(() => alert("Network error"));
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Add / Update Advance
  const saveAdvance = () => {
    if (!newAdvance.staff_id || !newAdvance.amount) {
      return alert("Select staff & amount");
    }
    const payload = {
      id:           advEditId,
      action:       advEditId ? 'update' : 'add',
      advance_date: billDate,
      staff_id:     parseInt(newAdvance.staff_id, 10),
      amount:       parseFloat(newAdvance.amount)
    };
    fetch(SAVE_ADV_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(r => r.json())
      .then(d => {
        if (d.status !== 'success') {
          return alert("Error: " + d.message);
        }
        // 1) Reload advances
        fetch(`${GET_ADV_API}?date=${billDate}`)
          .then(r => r.json())
          .then(setAdvances);

        // 2) Recompute the correct billing‐entry text
        const staffName = staffList.find(s => s.id === payload.staff_id)?.name || 'Staff';
        const advanceId = advEditId || d.id;
        const descBase  = `Advance #${advanceId} for ${staffName}`;

        if (!advEditId) {
          // newly added → append new “expense” entry
          setEntries(es => [
            ...es,
            { type: 'expense', amount: String(payload.amount), description: descBase }
          ]);
        } else {
          // edited existing advance → find and update the matching entry
          setEntries(es => es.map(e =>
            e.description.startsWith(`Advance #${advanceId} `)
              ? { ...e, amount: String(payload.amount), description: descBase }
              : e
          ));
        }

        // 3) Close dialog and reset
        setAdvEditOpen(false);
        setAdvEditId(null);
        setNewAdvance({ staff_id: '', amount: '' });
      })
      .catch(() => alert("Network error"));
  };

  // Delete Advance
  const deleteAdvance = id => {
    if (!window.confirm("Delete this advance?")) return;
    fetch(DELETE_ADV_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'delete', id })
    })
      .then(r => r.json())
      .then(d => {
        if (d.status !== 'success') {
          return alert("Error: " + d.message);
        }
        // remove from advances list
        setAdvances(a => a.filter(x => x.id !== id));
        // remove the matching “Advance #…” entry from entries
        setEntries(es => es.filter(e => !e.description.startsWith(`Advance #${id} `)));
      })
      .catch(() => alert("Network error"));
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Entry modal logic (Add / Edit one “entry”)
  const openEntry = idx => {
    if (idx >= 0) {
      // Editing an existing entry: detect if it is one of our DESCRIPTION_PRESETS
      const e = entries[idx];
      const isPreset = DESCRIPTION_PRESETS.includes(e.description);
      setNewEntry({
        type:        e.type,
        amount:      String(e.amount),
        description: isPreset ? e.description : e.description,
        isOther:     !isPreset
      });
      setEntryEditIndex(idx);
    } else {
      // New entry
      setNewEntry({ type: 'income', amount: '', description: '', isOther: false });
      setEntryEditIndex(-1);
    }
    setEntryModalOpen(true);
  };
  const closeEntry = () => setEntryModalOpen(false);

  const saveEntry = () => {
    if (!newEntry.amount) return alert("Enter amount");
    if (!newEntry.description.trim()) return alert("Enter description");
    const upd = [...entries];
    const obj = {
      type:        newEntry.type,
      amount:      newEntry.amount,
      description: newEntry.description
    };
    if (entryEditIndex >= 0) {
      upd[entryEditIndex] = obj;
    } else {
      upd.push(obj);
    }
    setEntries(upd);
    closeEntry();
  };
  const deleteEntry = i => {
    const upd = [...entries];
    upd.splice(i, 1);
    setEntries(upd);
  };

  // ───────────────────────────────────────────────────────────────────────────
  // Persist the entire billing record for this date
  const saveRecord = () => {
    const payload = {
      bill_date:             billDate,
      total_sales_today:     refTotal,
      total_tips:            parseFloat(totalTips) || 0,
      total_service_charges: parseFloat(totalServiceCharges) || 0,
      entries,
      cash_on_cashier:       refTotal + incTotal - expNonAdv - advTotal  // (or simply incTotal - expNonAdv if you want)
    };
    fetch(API_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    })
      .then(r => r.json())
      .then(d => {
        if (d.status === 'success') {
          alert("Saved successfully");
        } else {
          alert("Error: " + d.message);
        }
      })
      .catch(() => alert("Network error"));
  };

  // ───────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" gutterBottom>Billing Book</Typography>

      {/* Date Picker + “30th of April 2025” formatting */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <TextField
          label="Date"
          type="date"
          size="small"
          InputLabelProps={{ shrink: true }}
          value={billDate}
          onChange={e => setBillDate(e.target.value)}
        />
        <Button
          startIcon={<RefreshIcon />}
          variant="contained"
          onClick={() => setBillDate(billDate)}
        >
          Refresh
        </Button>
        <Typography variant="body2" sx={{ ml: 2 }}>
          {formatDateVerbose(billDate)}
        </Typography>
      </Box>

      {loading && <CircularProgress />}

      {/* ───────────── Overview Cards ───────────── */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {[
          {
            label:   'Sales (ref)',
            value:   refTotal,
            caption: null
          },
          {
            label:   'Additions',
            value:   incTotal,
            caption: refTotal > 0 ? `${((incTotal / refTotal) * 100).toFixed(1)}%` : '0.0%'
          },
          {
            label:   'Subtractions',
            // show only “non‐advance” subtractions:
            value:   expNonAdv,
            caption: refTotal > 0 ? `${((expNonAdv / refTotal) * 100).toFixed(1)}%` : '0.0%'
          },
          {
            label:   'Revenue',
            // “revenue” after adding incTotal, subtracting expNonAdv:
            value:   revenue,
            caption: null
          },
          {
            label:   'Advances',
            // show just total advances:
            value:   advTotal,
            caption: null
          },
          {
            label:   'Net Revenue',
            // revenue minus advances:
            value:   netRevenue,
            caption: null
          }
        ].map(stat => (
          <Box key={stat.label} sx={{ minWidth: 140 }}>
            <Typography variant="subtitle2">{stat.label}</Typography>
            <Typography variant="h6">Rs {fmt(stat.value)}</Typography>
            {stat.caption && (
              <Typography variant="caption" color="text.secondary">
                {stat.caption}
              </Typography>
            )}
          </Box>
        ))}
      </Paper>

      {/* ───────── Reference & Extras ───────── */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 4, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        {/* Sales (ref) */}
        <Box>
          <Typography>Total Sales</Typography>
          <TextField
            variant="standard"
            type="number"
            value={totalSalesToday}
            onChange={e => setTotalSalesToday(e.target.value)}
          />
        </Box>

        {/* Total Tips */}
        <Box>
          <Typography>Total Tips</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              variant="standard"
              type="number"
              value={totalTips}
              onChange={e => setTotalTips(e.target.value)}
            />
            <Button
              onClick={() => {
                if (!totalTips || isNaN(parseFloat(totalTips))) return;
                setEntries(es => [
                  ...es,
                  { type: 'income', amount: totalTips, description: 'Total Tips' }
                ]);
              }}
              variant="outlined"
              size="small"
            >
              Add to Entries
            </Button>
          </Box>
        </Box>

        {/* Service Charges */}
        <Box>
          <Typography>Service Charges</Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              variant="standard"
              type="number"
              value={totalServiceCharges}
              onChange={e => setTotalServiceCharges(e.target.value)}
            />
            <Button
              onClick={() => {
                if (!totalServiceCharges || isNaN(parseFloat(totalServiceCharges))) return;
                setEntries(es => [
                  ...es,
                  { type: 'income', amount: totalServiceCharges, description: 'Service Charges' }
                ]);
              }}
              variant="outlined"
              size="small"
            >
              Add to Entries
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* ───────── Entries Table ───────── */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Entries</Typography>
          <Button onClick={() => openEntry(-1)} variant="contained">Add Entry</Button>
        </Box>
        {entries.length === 0
          ? <Typography sx={{ mt: 1 }}>No entries</Typography>
          : isMobile
            ? (
              <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {entries.map((e, i) => (
                  <Paper key={i} sx={{ p: 1 }}>
                    <Typography>
                      {e.description} ({typeLabel(e.type)})
                    </Typography>
                    <Typography>Rs {fmt(e.amount)}</Typography>
                    <Box sx={{ mt: 1 }}>
                      <Button size="small" onClick={() => openEntry(i)}>Edit</Button>
                      <Button size="small" color="error" onClick={() => deleteEntry(i)}>Delete</Button>
                    </Box>
                  </Paper>
                ))}
              </Box>
            )
            : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Amount</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map((e, i) => (
                    <TableRow key={i}>
                      <TableCell>{typeLabel(e.type)}</TableCell>
                      <TableCell>{e.description}</TableCell>
                      <TableCell>Rs {fmt(e.amount)}</TableCell>
                      <TableCell>
                        <Button size="small" onClick={() => openEntry(i)}>Edit</Button>
                        <Button size="small" color="error" onClick={() => deleteEntry(i)}>Delete</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )
        }
      </Paper>

      {/* ───────── Advance List ───────── */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button variant="contained" onClick={() => setAdvEditOpen(true)}>
            Add Advance
          </Button>
          <IconButton onClick={() => setAdvListOpen(true)}>
            <Badge badgeContent={advances.length} color="primary">
              <HelpIcon />
            </Badge>
          </IconButton>
        </Box>
      </Paper>

      {/* Advance List Dialog */}
      <Dialog
        open={advListOpen}
        onClose={() => setAdvListOpen(false)}
        fullWidth
      >
        <DialogTitle>Advances on {billDate}</DialogTitle>
        <DialogContent dividers>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Staff</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {advances.map(a => (
                <TableRow key={a.id}>
                  <TableCell>{a.staff_name}</TableCell>
                  <TableCell>Rs {fmt(a.amount)}</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setAdvEditId(a.id);
                        setNewAdvance({
                          staff_id: String(a.staff_id),
                          amount:   String(a.amount)
                        });
                        setAdvEditOpen(true);
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => deleteAdvance(a.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {!advances.length && (
                <TableRow>
                  <TableCell colSpan={3} align="center">No advances</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAdvListOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add / Edit Advance Dialog */}
      <Dialog
        open={advEditOpen}
        onClose={() => {
          setAdvEditOpen(false);
          setAdvEditId(null);
        }}
        fullWidth
      >
        <DialogTitle>{advEditId ? 'Edit Advance' : 'Add Advance'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            select
            label="Staff"
            variant="standard"
            value={newAdvance.staff_id}
            onChange={e => setNewAdvance(a => ({ ...a, staff_id: e.target.value }))}
          >
            {staffList.map(s => (
              <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Amount"
            type="number"
            variant="standard"
            value={newAdvance.amount}
            onChange={e => setNewAdvance(a => ({ ...a, amount: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setAdvEditOpen(false);
            setAdvEditId(null);
          }}>Cancel</Button>
          <Button onClick={saveAdvance} variant="contained">Save</Button>
        </DialogActions>
      </Dialog>

      {/* ───────── Cash Count ───────── */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Typography variant="h6">Cash Count</Typography>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Actual Cash"
            type="number"
            variant="standard"
            value={actualCash}
            onChange={e => setActualCash(e.target.value)}
          />
          <Typography>
            Difference:&nbsp;
            <Box component="span"
                 sx={{ color: cashDiff >= 0 ? 'green' : 'red', fontWeight:'bold' }}>
              Rs {fmt(cashDiff)} {cashDiff >= 0 ? '(Extra)' : '(Due)'}
            </Box>
          </Typography>
          <Button variant="contained" onClick={saveCashCount}>
            Save Cash Count
          </Button>
        </Box>
      </Paper>

      {/* ───────── Save Full Records ───────── */}
      <Box sx={{ mt: 2 }}>
        <Button variant="contained" onClick={saveRecord}>
          Save Full Records
        </Button>
      </Box>

      {/* ───────── Entry Modal ───────── */}
      <Dialog open={entryModalOpen} onClose={closeEntry} fullWidth>
        <DialogTitle>
          {entryEditIndex >= 0 ? 'Edit Entry' : 'Add Entry'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            select
            label="Type"
            variant="standard"
            value={newEntry.type}
            onChange={e => setNewEntry(ne => ({ ...ne, type: e.target.value }))}
          >
            <MenuItem value="income">Add</MenuItem>
            <MenuItem value="expense">Subtract</MenuItem>
            <MenuItem value="card">Card/Online</MenuItem>
            <MenuItem value="none">None</MenuItem>
          </TextField>

          {/* Description dropdown w/ “Other” */}
          <TextField
            select
            label="Description"
            variant="standard"
            value={newEntry.isOther ? 'other' : newEntry.description}
            onChange={e => {
              const v = e.target.value;
              if (v === 'other') {
                setNewEntry(ne => ({ ...ne, isOther: true, description: '' }));
              } else {
                setNewEntry(ne => ({ ...ne, isOther: false, description: v }));
              }
            }}
          >
            {DESCRIPTION_PRESETS.map(opt => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
            <MenuItem value="other">Other</MenuItem>
          </TextField>

          {/* Free‐text only when “Other” is selected */}
          {newEntry.isOther && (
            <TextField
              label="Enter description"
              variant="standard"
              value={newEntry.description}
              onChange={e => setNewEntry(ne => ({ ...ne, description: e.target.value }))}
              fullWidth
            />
          )}

          <TextField
            label="Amount"
            type="number"
            variant="standard"
            value={newEntry.amount}
            onChange={e => setNewEntry(ne => ({ ...ne, amount: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEntry}>Cancel</Button>
          <Button onClick={saveEntry} variant="contained">
            {entryEditIndex >= 0 ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
